import type { AppDispatch } from "_shared/_stores/store-types";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { bookmarksApi } from "marketplace/bookmarks/_api/bookmark-api";
import {
  getCompanyBookmarkByCompanyId,
  getHasCompanyBookmarkStatusFailed,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import type { BookmarkedCompanyJSON } from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

/** The most ids that `POST marketplace/company-bookmarks/by-company-ids` accepts in one request. */
export const COMPANY_BOOKMARK_STATUS_BATCH_SIZE = 100;

const pendingCompanyIds = new Set<string>();
const requestedCompanyIds = new Set<string>();
let isFlushScheduled = false;

/**
 * Asks the server whether the session user has bookmarked this company. The ids that components
 * ask for in the same tick go in one bulk request: a marketplace results page shows a bookmark
 * button on each card, and one request for each card would be an N+1 across the network.
 * An id is asked once until its request ends; the answer goes into the bookmarks slice.
 */
export function requestCompanyBookmarkStatus(dispatch: AppDispatch, companyId: Guid): void {
  if (pendingCompanyIds.has(companyId.value) || requestedCompanyIds.has(companyId.value)) return;
  pendingCompanyIds.add(companyId.value);
  if (isFlushScheduled) return;
  isFlushScheduled = true;
  setTimeout(() => flushCompanyBookmarkStatusRequests(dispatch), 0);
}

function flushCompanyBookmarkStatusRequests(dispatch: AppDispatch): void {
  isFlushScheduled = false;
  const companyIds = [...pendingCompanyIds];
  pendingCompanyIds.clear();

  for (let start = 0; start < companyIds.length; start += COMPANY_BOOKMARK_STATUS_BATCH_SIZE) {
    const batch = companyIds.slice(start, start + COMPANY_BOOKMARK_STATUS_BATCH_SIZE);
    for (const companyId of batch) requestedCompanyIds.add(companyId);
    const request = dispatch(
      bookmarksApi.endpoints.getCompanyBookmarksByCompanyIds.initiate(
        { companyIds: batch.map((companyId) => new Guid(companyId)) },
        // Asked only when the slice has no answer, so a cached reply is never the one wanted. RTK
        // Query keeps the last reply for these ids a minute after the request ends, and without
        // this would return it unsent.
        { forceRefetch: true },
      ),
    );
    void request.finally(() => {
      for (const companyId of batch) requestedCompanyIds.delete(companyId);
      request.unsubscribe();
    });
  }
}

export type CompanyBookmarkStatus = {
  /** The bookmark, null when there is none, or undefined while the answer loads or after it failed. */
  bookmark: BookmarkedCompanyJSON | null | undefined;
  /** The ask failed. It is not repeated by itself, so that an outage is not asked about in a loop. */
  hasFailed: boolean;
  /** Asks again after a failure. */
  retry: () => void;
};

/**
 * The session user's bookmark of this company. Pass no id, or `skip`, to ask nothing.
 */
export default function useCompanyBookmarkStatus(
  companyId?: Guid,
  options?: { skip?: boolean },
): CompanyBookmarkStatus {
  const dispatch = useAttorneyHubDispatch();
  const bookmark = useSelector(getCompanyBookmarkByCompanyId(companyId));
  const failed = useSelector(getHasCompanyBookmarkStatusFailed(companyId));
  const skip = options?.skip ?? false;
  const companyIdValue = companyId?.value;

  useEffect(() => {
    if (skip || !companyIdValue || bookmark !== undefined || failed) return;
    requestCompanyBookmarkStatus(dispatch, new Guid(companyIdValue));
  }, [bookmark, dispatch, failed, companyIdValue, skip]);

  const retry = useCallback(() => {
    if (skip || !companyIdValue) return;
    requestCompanyBookmarkStatus(dispatch, new Guid(companyIdValue));
  }, [dispatch, companyIdValue, skip]);

  if (skip) return { bookmark: undefined, hasFailed: false, retry };
  return { bookmark, hasFailed: failed, retry };
}
